import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Avatar,
  Chip,
  Divider,
  alpha,
  LinearProgress,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IMAGES_API_URL } from "../constants";
import FormField from "./FormField";

// Form field validation builder (same as RegistrationPage)
const buildValidation = (f) => {
  let v = yup;
  if (f.type === "checkbox") {
    v = v.boolean();
  } else if (f.type === "select") {
    v = v.string();
  } else if (f.type === "mcq") {
    if (f.required) {
      return v.mixed().test({
        message: "Required",
        test: (v) => {
          return v?.length > 0;
        },
      });
    } else {
      return v.array().of(yup.string());
    }
  } else if (f.type === "radio" || f.type === "payment") {
    v = v.string();
  } else if (f.type === "number") {
    v = v.number();
  } else if (f.type === "email") {
    v = v.string().trim().email();
  } else if (f.type === "confirm_email") {
    v = v
      .string()
      .trim()
      .test({
        message: "Does not match",
        test: (val, p) => {
          if ("Email Address" in p.parent) {
            return val === p.parent["Email Address"];
          }
          return val === p.parent["Email"];
        },
      });
  } else {
    v = v.string().trim();
  }

  if (f.required) {
    v = v.required("Required");
  }

  if (
    f.maxLength &&
    !["checkbox", "radio", "payment", "select", "mcq"].includes(f.type)
  ) {
    v = v.max(
      f.maxLength,
      f.type === "number"
        ? `Max value: ${f.maxLength}`
        : `Max ${f.maxLength} characters allowed`
    );
  }
  if (
    f.minLength &&
    !["checkbox", "radio", "payment", "select", "mcq"].includes(f.type)
  ) {
    v = v.min(
      f.minLength,
      f.type === "number"
        ? `Min value: ${f.minLength}`
        : `Min ${f.minLength} characters required`
    );
  }
  if (f.pattern) {
    v = v.matches(f.pattern, "Invalid pattern!");
  }
  return v;
};

const SocialRegistrationForm = ({
  socialData,
  mappedData,
  missingFields = [],
  allFields = [],
  onSubmit,
  provider,
  loading,
  eventId,
  uid, // Add UID prop for invitation links
}) => {
  console.log("🔍 SocialRegistrationForm received uid:", uid);
  const theme = useTheme();
  const [submitError, setSubmitError] = useState(null);

  // State for form field management (similar to RegistrationPage)
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedMobileCode, setSelectedMobileCode] = useState(null);
  const [email, setEmail] = useState("");
  const [originalPrice, setOriginalPrice] = useState(0);
  const [price, setPrice] = useState(0);

  console.log("🔍 SocialRegistrationForm - Raw Props:", {
    socialData,
    mappedData,
    missingFields,
    allFields,
    provider,
  });

  // Parse social data safely
  const parsedSocialData =
    typeof socialData === "string" ? JSON.parse(socialData) : socialData;
  const parsedMappedData =
    typeof mappedData === "string" ? JSON.parse(mappedData) : mappedData;
  const parsedMissingFields =
    typeof missingFields === "string"
      ? JSON.parse(missingFields)
      : missingFields;

  // Show all fields but mark email as readonly
  const visibleFields = allFields.filter((field) => {
    // Check if field should be hidden
    if (field.sx) {
      const sx = typeof field.sx === "string" ? JSON.parse(field.sx) : field.sx;
      if (sx.display === "none" || sx.display === "none !important") {
        return false;
      }
    }
    return true;
  });

  // Check if field is an email field
  const isEmailField = (field) => {
    return (
      field.type === "email" ||
      field.name.toLowerCase().includes("email") ||
      field.label?.toLowerCase().includes("email")
    );
  };

  // Create validation schema
  const validationSchema = yup.object().shape({
    ...visibleFields.reduce(
      (a, r) => ({ ...a, [r.name]: buildValidation(r) }),
      {}
    ),
  });

  // Create default values
  const defaultValues = visibleFields.reduce((acc, field) => {
    let fieldValue;

    // For UID links, prioritize invitation data if available
    if (uid && parsedMappedData?.[field.name] !== undefined) {
      fieldValue = parsedMappedData[field.name];
    } else {
      fieldValue = field.defaultValue || "";
    }

    // For email fields, use verified email (but only if not from invitation data)
    if (isEmailField(field) && parsedSocialData?.email && !uid) {
      fieldValue = parsedSocialData.email;
    }

    acc[field.name] = fieldValue;
    return acc;
  }, {});

  // MOVE ALL HOOKS TO THE TOP - Fix React Hook Rules violation
  const { control, handleSubmit, setValue, errors, watch } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  console.log("🔍 SocialRegistrationForm - Parsed Data:", {
    parsedSocialData,
    parsedMappedData,
    socialDataType: typeof socialData,
    mappedDataType: typeof mappedData,
    socialDataLength: socialData?.length,
    mappedDataLength: mappedData?.length,
  });

  console.log("🔍 SocialRegistrationForm - Field Processing:", {
    allFieldsCount: allFields.length,
    visibleFieldsCount: visibleFields.length,
    visibleFieldNames: visibleFields.map((f) => f.name),
    allFieldNames: allFields.map((f) => f.name),
  });

  console.log("🔍 SocialRegistrationForm - Default Values:", defaultValues);

  // NOW handle conditional returns AFTER all hooks are called
  if (!parsedSocialData) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", py: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" color="error" gutterBottom>
            No Social Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Social authentication data is missing. Please try again.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const handleFormSubmit = async (formData) => {
    console.log("🔍 SocialRegistrationForm - Form Submit:", {
      formData,
      parsedMappedData,
      socialEmail: parsedSocialData?.email,
    });

    try {
      setSubmitError(null);
      const combinedData = {
        ...parsedMappedData,
        ...formData,
      };

      // Ensure social media email is included in the final data
      const socialEmail = parsedSocialData?.email;
      if (socialEmail) {
        combinedData.Email = socialEmail;
        combinedData.email = socialEmail;
      }

      console.log("🔍 SocialRegistrationForm - Combined Data:", combinedData);

      await onSubmit(combinedData);
    } catch (error) {
      console.error("🚨 Form submission error:", error);
      setSubmitError(
        error.message || "An error occurred while submitting the form"
      );
    }
  };

  const providerColors = {
    facebook: "#1877F2",
    linkedin: "#0A66C2",
    google: "#EA4335",
  };

  const totalFields = allFields.length;
  const completedFields = Object.keys(parsedMappedData || {}).length;
  const progressPercentage = (completedFields / totalFields) * 100;

  console.log("🔍 SocialRegistrationForm - Progress:", {
    totalFields,
    completedFields,
    progressPercentage,
    mappedDataKeys: Object.keys(parsedMappedData || {}),
  });

  // Helper function to find country code by dial code (same as RegistrationPage)
  const findCountryCodeByDialCode = (dialCode) => {
    return selectedCountry || "Oman";
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(
            providerColors[provider] || theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(
            providerColors[provider] || theme.palette.primary.main,
            0.2
          )}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            src={`${IMAGES_API_URL}logo/${eventId}.png`}
            sx={{
              width: 60,
              height: 60,
              backgroundColor: theme.palette.primary.main,
              border: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Avatar
            src={parsedSocialData?.picture}
            sx={{
              width: 64,
              height: 64,
              border: `3px solid ${
                providerColors[provider] || theme.palette.primary.main
              }`,
              ml: -1,
              boxShadow: theme.shadows[4],
              "& img": {
                objectFit: "cover",
              },
            }}
          >
            {parsedSocialData?.name ? (
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "white" }}
              >
                {parsedSocialData.name.charAt(0).toUpperCase()}
              </Typography>
            ) : (
              <PersonIcon sx={{ fontSize: 32 }} />
            )}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Welcome, {parsedSocialData?.name || parsedSocialData?.firstName}!
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {parsedSocialData?.email}
              </Typography>
            </Box>
            <Chip
              label={`Connected via ${provider}`}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: alpha(
                  providerColors[provider] || theme.palette.primary.main,
                  0.1
                ),
                color: providerColors[provider] || theme.palette.primary.main,
                textTransform: "capitalize",
              }}
            />
          </Box>
        </Box>

        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Registration Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {completedFields} of {totalFields} fields completed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.grey[400], 0.3),
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                backgroundColor:
                  providerColors[provider] || theme.palette.primary.main,
              },
            }}
          />
        </Box>
      </Paper>

      {parsedMappedData && Object.keys(parsedMappedData).length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.success.main, 0.05),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Pre-filled Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The following information has been automatically filled from your{" "}
            {provider} account. Email is locked for security. You can modify
            other fields as needed.
          </Typography>

          <Grid container spacing={2}>
            {Object.entries(parsedMappedData).map(([key, value]) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={key}
                sx={{
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.grey[100], 0.5),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Complete Your Registration
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Please review and complete all required fields below. Fields marked
          with an asterisk (*) are required.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2}>
            {visibleFields.map((field) => {
              console.log("🔍 Rendering field:", {
                name: field.name,
                type: field.type,
                required: field.required,
                defaultValue: field.defaultValue,
                isEmailField: isEmailField(field),
              });

              return (
                <React.Fragment key={field.name}>
                  <Grid
                    item
                    xs={field.xs || 12}
                    sm={field.sm || field.xs || 12}
                    md={field.md || field.xs || 12}
                    lg={field.md || field.xs || 12}
                  >
                    <Controller
                      name={field.name}
                      control={control}
                      render={(rhf) => {
                        try {
                          return (
                            <FormField
                              rhf={rhf}
                              f={{
                                ...field,
                                sx: field.sx ? JSON.parse(field.sx) : {},
                              }}
                              setValue={setValue}
                              findCountryCodeByDialCode={
                                findCountryCodeByDialCode
                              }
                              selectedCountry={selectedCountry}
                              setSelectedCountry={setSelectedCountry}
                              selectedMobileCode={selectedMobileCode}
                              setSelectedMobileCode={setSelectedMobileCode}
                              parsedRegistration={null}
                              pmt={null}
                              setEmail={setEmail}
                              isEmailTaken={null}
                              emailCheckLoading={false}
                              showFamilyMembers={false}
                              setOriginalPrice={setOriginalPrice}
                              setPrice={setPrice}
                              promoDiscount={0}
                              memberDiscount={0}
                              isMemberIdValid={false}
                              uid={uid}
                              customTheme={null}
                              featuresList={[]}
                              prefilledEmail={parsedSocialData?.email}
                              socialProvider={provider}
                            />
                          );
                        } catch (error) {
                          console.error(
                            `🚨 Error rendering field ${field.name}:`,
                            error
                          );
                          return (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              Error rendering field: {field.name} -{" "}
                              {error.message}
                            </Alert>
                          );
                        }
                      }}
                    />
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              size="large"
              sx={{
                minWidth: 200,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                backgroundColor:
                  providerColors[provider] || theme.palette.primary.main,
                "&:hover": {
                  backgroundColor:
                    providerColors[provider] || theme.palette.primary.dark,
                },
              }}
            >
              Complete Registration
            </LoadingButton>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default SocialRegistrationForm;
